import { Box, CardMedia, CircularProgress, Collapse, Divider, ImageList, ImageListItem, List, ListItemButton, ListItemIcon, ListItemText, Modal, Paper, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import ErrorTypography from "../../shared/ErrorTypography/ErrorTypography";
import { useGetResultQuery, useGetServiceResultQuery } from "../../store/data/dataSlice";
import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import useWindowDimensions from "../../shared/hooks/useWindowDimensions";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { togglePauseTut } from "../../store/tutorial/tutorialSlice";

type Props = {
    rid: string;
    sid: string;
}

const ServiceResult = ({ rid, sid }: Props): JSX.Element => {
    const { t } = useTranslation();

    const service = useGetServiceResultQuery({rid, sid});
    const result = useGetResultQuery({rid: rid})
    const [zoomOpen, setZoomOpen] = useState(false);
    const [image, setImage] = useState(null as HTMLImageElement | null);
    const [imageScale, setImageScale] = useState(1 as number);
    const [vpOffsetX, setVpOffsetX] = useState(0 as number);
    const [vpOffsetY, setVpOffsetY] = useState(0 as number);
    const [lastCursorPos, setLastCursorPos] = useState({x: null, y: null} as {x: number | null, y:number | null});
    const [panning, setPanning] = useState(false);
    const [imageBaseScale, setImageBaseScale] = useState(1);
    const windowDimensions = useWindowDimensions();
    const tutorial = useAppSelector(state => state.tutorial);
    const dispatch = useAppDispatch();


    useEffect(()=>{
        if(service.data) {
            if(service.data.data) {
                const im = new Image;
                im.src = `data:image/png;base64,${service.data.data}`;
                setImage(im);
            }
        }
    }, [service])

    useEffect(()=>{
        if(image === null) return;
        const baseScaleFactor = getBaseScaleFactor(image.height, image.width);
        setImageBaseScale(baseScaleFactor);
        console.log(baseScaleFactor);
    }, [image])

    const getBaseScaleFactor = (height: number, width: number) => {
            console.log(height, width);
            let resizeFactor = 1;
            if((height != windowDimensions.height*0.90) && (height !== 0)) {
                resizeFactor = (windowDimensions.height*0.90)/height;
            }
            if((resizeFactor*width > windowDimensions.width*0.90) && (width !== 0)) {
                resizeFactor = (windowDimensions.width*0.90)/width;
            }
            return resizeFactor;
    }

    const onStartPanning = (e: React.MouseEvent) => {
        const currentBoundingBox = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - currentBoundingBox.left;
        const relY = e.clientY - currentBoundingBox.top;

        setPanning(true);
        setLastCursorPos({x: relX, y: relY});
    }

    const onStopPanning = (e: React.MouseEvent) => {
        setPanning(false);
    }

    const onDrag = (e: React.MouseEvent) => {
        if(image === null) return;
        const currentBoundingBox = e.currentTarget.getBoundingClientRect();
        const currentPos = {x: e.clientX - currentBoundingBox.left, y: e.clientY - currentBoundingBox.top};

        const maxOffsetY = -vpOffsetY;
        const minOffsetY = maxOffsetY - (image.height*imageBaseScale*(imageScale-1));
        const maxOffsetX = -vpOffsetX;
        const minOffsetX = maxOffsetX - (image.width*imageBaseScale*(imageScale-1));

        let lastPos = lastCursorPos;
        if(lastCursorPos.x === null || lastCursorPos.y === null) lastPos = {x: 0, y: 0};
        if(panning && lastPos.x !== null && lastPos.y !== null) {
            let panX = currentPos.x - lastPos.x;
            if(panX > maxOffsetX) panX = maxOffsetX;
            else if(panX < minOffsetX) panX = minOffsetX;

            let panY = currentPos.y - lastPos.y;
            if(panY > maxOffsetY) panY = maxOffsetY;
            else if(panY < minOffsetY) panY = minOffsetY;

            const newVpOffsetX = vpOffsetX + panX;
            const newVpOffsetY = vpOffsetY + panY;
            setVpOffsetX(newVpOffsetX);
            setVpOffsetY(newVpOffsetY);
        }
        setLastCursorPos(currentPos)
    }

    const onZoom = (e: React.WheelEvent) => {
        if(image === null) return;

        const currentBoundingBox = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - currentBoundingBox.left;
        const relY = e.clientY - currentBoundingBox.top;
        let newScale = imageScale;
        let deltaX;
        let deltaY;

        if(lastCursorPos.x !== null && lastCursorPos.y !== null) {
            deltaX = relX - lastCursorPos.x;
            deltaY = relY - lastCursorPos.y;
        }
        else {
            deltaX = 0;
            deltaY = 0;
        }

        if(e.deltaY < 0 && imageScale < 4) {
            newScale = imageScale + 0.2;
        }
        else if(e.deltaY > 0 && imageScale > 1) {
            newScale = imageScale - 0.2;
        }

        setImageScale(newScale);
        const newVpOffsetX = -((relX-(deltaX/3)) * (newScale-1));
        const newVpOffsetY = -((relY-(deltaY/3)) * (newScale-1));

        setLastCursorPos({x: relX, y: relY})
        setVpOffsetX(newVpOffsetX);
        setVpOffsetY(newVpOffsetY);
    }

    const handleClick = () => {
        setZoomOpen(true);
        if(tutorial.active && !tutorial.paused && tutorial.stepIndex === 29) {
            dispatch(togglePauseTut());
        }
    }

    const handleClose = () => {
        setZoomOpen(false);
        setImageScale(1);
        setVpOffsetX(0);
        setVpOffsetY(0);
        setLastCursorPos({x: null, y:null});
        setPanning(false);
    }

    if (service.error) {
        return <ErrorTypography message={t('Error getting results')} />;
    }
    else {
        return (
            <Paper elevation={2} sx={{ px: 2, py: 2 }}>
                {((!service.isLoading && service.data) && (!result.isLoading && result.data) && image !== null) ? (
                    <Stack direction={'row'} spacing={2}>
                        <Stack direction={'column'} sx={{width: '55vw', p: '1rem'}}>
                            {/* eslint-disable-next-line */}
                            <img 
                                className="result-serviceImage"
                                src={image.src}
                                alt='result_img'
                                style={{ maxHeight: '80vh', objectFit:'contain', paddingBottom: '1rem', cursor:'zoom-in'}}
                                onClick={handleClick}
                            />
                            <Modal open={zoomOpen} onClose={handleClose}>
                                {/* eslint-disable-next-line */}
                                <div 
                                onWheel={(e) => onZoom(e)}
                                onMouseDown={(e) => onStartPanning(e)}
                                onMouseMove={(e) => onDrag(e)}
                                onMouseUp={(e) => onStopPanning(e)}
                                onMouseLeave={(e) => onStopPanning(e)}
                                style={{
                                    height: image.height*imageBaseScale,
                                    width: image.width*imageBaseScale,
                                    aspectRatio: `${image.width}/${image.height}`,
                                    position:'absolute',
                                    top:'50%',
                                    left:'50%',
                                    transform: 'translate(-50%, -50%)',
                                    maxWidth: '90vw',
                                    maxHeight: '90vh',
                                    userSelect: 'none',
                                    overflow: 'clip',
                                }}>
                                    {/* eslint-disable-next-line */}
                                    <img
                                        unselectable='on'
                                        draggable={false}
                                        src={image.src}
                                        alt='result_img'
                                        style={{ objectFit:'contain', outline:'none', height:image.height*imageScale*imageBaseScale, width: image.width*imageScale*imageBaseScale,
                                            transform:`translate(${vpOffsetX}px, ${vpOffsetY}px)`}}
                                    />
                                </div>
                            </Modal>
                            <Divider />
                            <div className="results-serviceInfoList">
                                <Typography variant={'h6'}> Service Information </Typography>
                                <List>
                                    <ListItemText primary={"Pipeline: " + service.data.pipeline}/>
                                    <ListItemText primary={"Name: " + service.data.service.name}/>
                                    <ListItemText primary={t("Type") + ": " + service.data.service.type}/>
                                    <ListItemText primary={t('Parameters') + ':'} />
                                    {service.data.service.parameters !== null ? (
                                        Object.keys(service.data.service.parameters).length !== 0 ? (
                                            Object.keys(service.data.service.parameters).map(key => (
                                                <ListItemText primary={key + ': ' + service.data.service.parameters[key]} sx={{pl: '1rem'}}/>
                                            ))
                                        ) : 
                                        (
                                            <ListItemText primary={'None'} sx={{pl: '1rem'}}/>
                                        )
                                    ) : 
                                    (
                                        <ListItemText primary={'None'} sx={{pl: '1rem'}}/>
                                    )
                                    }
                                </List>
                            </div>
                        </Stack>
                        <Divider orientation='vertical'/>
                        <Paper style={{maxHeight:'100vh', overflow:'auto', padding:'1rem'}} elevation={0}>
                            <div className="results-serviceQuickNavMenu">
                                <Typography variant="h5"><Trans>Service List</Trans>:</Typography>
                                <Divider />
                                <List>
                                    {result.data.map(service => (
                                        <ListItemButton 
                                        component={Link} 
                                        to={`/results/${service.result_id}/${service.service_id}`} 
                                        sx={{border:service.service_id === sid ? (1) : (0), p: 1, borderRadius: '1%'}}>
                                            <ListItemIcon>
                                                <img
                                                src={`data:image/png;base64,${service.data_small}`}
                                                alt={"xd"}                            
                                                style={{ maxHeight:'15vh', objectFit:'contain', alignContent:'center', maxWidth:'7vw', paddingRight: 10}}
                                                />
                                            </ListItemIcon>
                                            <ListItemText primary={service.service.name} secondary={service.service.type}/>
                                        </ListItemButton>
                                    ))}
                                </List>
                            </div>
                        </Paper>
                    </Stack>
                ) : (
                    <Box sx={{ p: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        )
    }
}

export default ServiceResult;